import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { resolveGameStoreHook } from '@core/gameengine/state/activeGameStore';
import { useAuth } from '@core/gameengine/state/auth';
import { useChat, getUnreadChatCount } from '@core/gameengine/state/chat';
import {
  formatUnreadChatCount,
  getChatUnreadCountBadgeStyle,
  getChatUnreadDotStyle,
} from './chatUnreadIndicator';
import { GameMenu } from './Menu';
import './Header.css';
import { WalletBalance } from '@core/UI/wallet/WalletBalance';
import {
  GamingSessionTimers,
  GamingSessionNet,
} from '@core/UI/overlayelements/GamingSessionStatus';
import { useGamingSessionStore } from '@core/regulatory';
import { GlobalBus, UI_DISMISS_POPUPS } from '@core/Bus/EventBus';
import { getGameFromUrl } from '@core/utils/urlParser';
import { isSkyxNewRoute } from '@core/utils/gameRouteSlugs';
import {
  getAppBasePath,
  getStaticAssetOrigin,
} from '@core/utils/assetFolderSlug';
import { useT } from '@core/i18n';
import {
  SKYX_NEW_CLOSE_CHAT,
  SKYX_NEW_CLOSE_MENU,
} from '@core/lobby/skyxNewPanelEvents';
const getInitialGameName = () => getGameFromUrl();

/** Must match @games/skyx_new/events/skyxNewMenuEvents (core cannot import games). */
const SKYX_NEW_OPEN_GAME_RULES = 'skyx_new:open-game-rules';
const SKYX_NEW_OPEN_HOW_TO_PLAY = 'skyx_new:open-how-to-play';
const SKYX_NEW_OPEN_PROVABLY_FAIR_SETTINGS =
  'skyx_new:open-provably-fair-settings';
const SKYX_NEW_OPEN_GAME_LIMITS = 'skyx_new:open-game-limits';
const SKYX_NEW_OPEN_CHANGE_AVATAR = 'skyx_new:open-change-avatar';

const SKYX_NEW_MENU_CHROME = {
  playerHeaderBg: 'linear-gradient(180deg, #140033 0%, #1F0A4D 100%)',
  bodyBg: 'linear-gradient(180deg, #140033 0%, #1F0A4D 100%)',
  switchChrome: {
    trackOff: '#2D174F',
    trackOn: '#A83DFF',
    thumbOff: '#7660A5',
    thumbOn: '#FFFFFF',
    trackOnGlow: '0 0 8px rgba(168, 61, 255, 0.4)',
    labelOff: '#AFA2D0',
    labelOn: '#FFFFFF',
  },
  /** Header bar background */
  headerBackground: '#170550',
  /** Header balance numeric amount */
  headerBalanceAmount: '#13B42E',
} as const;

const SKYX_NEW_MENU_HANDLERS = {
  onGameRules: () => GlobalBus.emit(SKYX_NEW_OPEN_GAME_RULES),
  onHowToPlay: () => GlobalBus.emit(SKYX_NEW_OPEN_HOW_TO_PLAY),
  onProvablyFairSettings: () =>
    GlobalBus.emit(SKYX_NEW_OPEN_PROVABLY_FAIR_SETTINGS),
  onGameLimits: () => GlobalBus.emit(SKYX_NEW_OPEN_GAME_LIMITS),
  onChangeAvatar: () => GlobalBus.emit(SKYX_NEW_OPEN_CHANGE_AVATAR),
} as const;

const SKYX_NEW_MENU_CHROME_PROPS = {
  playerHeaderBackground: SKYX_NEW_MENU_CHROME.playerHeaderBg,
  menuBodyBackground: SKYX_NEW_MENU_CHROME.bodyBg,
  playerHeaderLabelColor: '#ffffff',
  switchChrome: SKYX_NEW_MENU_CHROME.switchChrome,
} as const;

function buildSkyxMenuChrome(
  platformUIConfig: Record<string, unknown>
): typeof SKYX_NEW_MENU_CHROME_PROPS & { playerAvatarSizePx?: number } {
  const sizePx = platformUIConfig.menuPlayerAvatarSizePx;
  return {
    ...SKYX_NEW_MENU_CHROME_PROPS,
    ...(typeof sizePx === 'number' ? { playerAvatarSizePx: sizePx } : {}),
  };
}

export const GameHeader: React.FC = () => {
  const t = useT();
  const { user, isDemo, logout } = useAuth();
  const hasUnreadChat = useChat(state => state.hasUnread);
  const unreadChatCount = useChat(getUnreadChatCount);
  const [gameName, setGameName] = useState<string | null>(getInitialGameName());
  const activeController = (
    window as any
  ).playgrid?.getCurrentGameController?.();
  const useGameStore = resolveGameStoreHook(activeController);
  const platformUIConfig = activeController?.getPlatformUIConfig?.() || {};

  const activeState = useGameStore((s: any) => ({
    setBalance: s.setBalance,
    assetManager: s.assetManager,
    deviceType: s.deviceType,
    isLandscape: s.isLandscape,
  }));
  const { setBalance, assetManager, deviceType, isLandscape } = activeState;
  const useViewportFixedMenu =
    deviceType === 'mobile' || deviceType === 'tablet';
  // Debug: Log user data
  useEffect(() => {
    console.log('[Header] Current user:', user);
    console.log('[Header] Is demo:', isDemo);
  }, [user, isDemo]);
  const [isMenuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuPortalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onNav = () => {
      const currentGameName = getGameFromUrl();
      setGameName(currentGameName);
    };
    window.addEventListener('playgrid:navigation', onNav as any);
    return () =>
      window.removeEventListener('playgrid:navigation', onNav as any);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (menuRef.current?.contains(target)) return;
      if (menuPortalRef.current?.contains(target)) return;
      setMenuOpen(false);
    };
    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen]);

  useEffect(() => {
    if (!isSkyxNewRoute(gameName)) return;
    return GlobalBus.on(SKYX_NEW_CLOSE_MENU, () => setMenuOpen(false));
  }, [gameName]);

  useEffect(() => {
    return GlobalBus.on(UI_DISMISS_POPUPS, () => setMenuOpen(false));
  }, []);

  const toggleMenu = () => {
    setMenuOpen(prev => {
      if (!prev && isSkyxNewRoute(gameName)) {
        GlobalBus.emit(SKYX_NEW_CLOSE_CHAT, {});
      }
      return !prev;
    });
  };

  const handleChatToggle = () => {
    if (isSkyxNewRoute(gameName)) {
      setMenuOpen(false);
    }
    GlobalBus.emit('chat:toggle', {});
  };

  // NEW: Handle redirection
  const handleGoLobby = () => {
    const playgrid = (window as any).playgrid;
    if (playgrid?.goLobby) playgrid.goLobby();
    setMenuOpen(false);
  };

  const handleDemoStart = () => {
    // Set demo balance and start demo mode
    setBalance(1000);
    console.log('Demo mode started');
  };

  const staticOrigin = assetManager?.baseUrl ?? getStaticAssetOrigin();

  const resolveLogoPath = (): string => {
    if (platformUIConfig.headerLogoSrc) {
      return platformUIConfig.headerLogoSrc;
    }
    if (isSkyxNewRoute(gameName)) {
      return '/games/skyx_new/images/ui/logo_1_Text.webp';
    }
    if (gameName === 'nflkick') {
      return '/assets/lobbyAssets/nflkick.jpg'; // Temporary fallback
    }
    if (gameName) {
      return `/games/${gameName}/images/logo.webp`;
    }
    return '/games/crash/images/logo.png';
  };

  const rawLogoPath = resolveLogoPath();
  const logoSrc = (() => {
    if (
      rawLogoPath.startsWith('http://') ||
      rawLogoPath.startsWith('https://')
    ) {
      return rawLogoPath;
    }

    const normalized = rawLogoPath.startsWith('/')
      ? rawLogoPath
      : `/${rawLogoPath}`;
    const basePath = getAppBasePath();

    // Avoid double base when path already includes Vite base.
    if (basePath && normalized.startsWith(`${basePath}/`)) {
      const originOnly = staticOrigin.endsWith(basePath)
        ? staticOrigin.slice(0, -basePath.length)
        : staticOrigin.replace(new RegExp(`${basePath}$`), '');
      return `${originOnly}${normalized}`;
    }

    return `${staticOrigin}${normalized}`;
  })();

  const inGame = !!gameName;
  const isSkyxNew = isSkyxNewRoute(gameName);
  const menuIconFill = 'rgba(255, 255, 255, 0.75)';

  const isDesktop = window.innerWidth >= 1024;

  let logoHeight = '30px';

  if (gameName === 'chickenluck') {
    logoHeight = isDesktop ? '36px' : '42px';
  } else if (inGame && isDesktop) {
    logoHeight = '24px';
  } else if (deviceType === 'mobile' || deviceType === 'tablet') {
    logoHeight = '36px';
  }

  const configuredLogoHeights = platformUIConfig.headerLogoHeightPx as
    | Partial<Record<'desktop' | 'tablet' | 'mobile', number>>
    | undefined;
  if (isSkyxNew && configuredLogoHeights) {
    const deviceKey: 'desktop' | 'tablet' | 'mobile' =
      deviceType === 'tablet'
        ? 'tablet'
        : deviceType === 'mobile'
          ? 'mobile'
          : 'desktop';
    const configuredPx =
      configuredLogoHeights[deviceKey] ?? configuredLogoHeights.desktop;
    if (typeof configuredPx === 'number' && configuredPx > 0) {
      logoHeight = `${configuredPx}px`;
    }
  }

  const logoStyle: React.CSSProperties = {
    ...styles.logo,
    height: logoHeight,
    width: 'auto',
    flexShrink: 0,
  };

  const sessionActive = useGamingSessionStore(
    s => s.policy.sessionDisplays && s.sessionStartedAtMs != null
  );
  const isHandheldHeader = deviceType === 'mobile' || deviceType === 'tablet';

  const useStackedSessionHeader =
    inGame && sessionActive && isHandheldHeader && !isLandscape;

  const headerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;

    const syncHeaderHeightVar = () => {
      const h = el.getBoundingClientRect().height;
      if (h > 0) {
        document.documentElement.style.setProperty(
          '--headerHeightDynamic',
          `${Math.round(h)}px`
        );
      }
    };

    syncHeaderHeightVar();
    const ro = new ResizeObserver(syncHeaderHeightVar);
    ro.observe(el);
    return () => {
      ro.disconnect();
      document.documentElement.style.setProperty(
        '--headerHeightDynamic',
        '40px'
      );
    };
  }, [useStackedSessionHeader]);

  const balanceContainerStyle: React.CSSProperties = {
    ...(typeof platformUIConfig.headerBalanceMenuGapPx === 'number'
      ? {
          marginRight: 0,
          marginInlineEnd: `${platformUIConfig.headerBalanceMenuGapPx}px`,
        }
      : {}),
    ...(isHandheldHeader && sessionActive
      ? {
          height: '22px',
          padding: '0 6px',
          marginInlineEnd: '4px',
        }
      : {}),
  };

  const balanceAmountStyle: React.CSSProperties = {
    ...(platformUIConfig.balanceAmountStyle || {}),
    ...(isSkyxNew ? { color: SKYX_NEW_MENU_CHROME.headerBalanceAmount } : {}),
    ...(isHandheldHeader && sessionActive ? { fontSize: '10px' } : {}),
  };

  const balanceCurrencyStyle: React.CSSProperties =
    isHandheldHeader && sessionActive ? { fontSize: '9px' } : {};

  return (
    <div
      id="game-header"
      ref={headerRef}
      style={{
        ...styles.headerContainer,
        ...(platformUIConfig.headerContainerStyle || {}),
        ...(isSkyxNew
          ? { background: SKYX_NEW_MENU_CHROME.headerBackground }
          : {}),
        ...(useStackedSessionHeader
          ? {
              height: 'auto',
              minHeight: '38.5px',
              flexDirection: 'column' as const,
              alignItems: 'stretch',
              justifyContent: 'center',
              gap: '4px',
              paddingTop: '4px',
              paddingBottom: '4px',
            }
          : {}),
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          minWidth: 0,
          gap: isHandheldHeader ? 6 : 12,
        }}
      >
        <div
          style={{
            ...styles.leftSection,
            gap: isHandheldHeader ? 6 : 12,
            minWidth: 0,
            flex: '1 1 auto',
          }}
        >
          <img
            src={logoSrc}
            alt="Logo"
            style={logoStyle}
            onError={e => {
              console.error('[Header] Failed to load logo:', logoSrc);
              console.error('[Header] Image error:', e);
            }}
          />
          {inGame && !useStackedSessionHeader && <GamingSessionTimers />}
        </div>
        <div
          style={{
            ...styles.rightSection,
            gap: isHandheldHeader ? 4 : 10,
            flexShrink: 0,
          }}
          ref={menuRef}
        >
          {inGame && (
            <>
              <GamingSessionNet />
              <WalletBalance
                variant="header"
                containerStyle={balanceContainerStyle}
                amountStyle={balanceAmountStyle}
                currencyStyle={balanceCurrencyStyle}
              />

              <div style={{ position: 'relative' }}>
                <button
                  type="button"
                  className="header-icon-btn"
                  style={styles.menuButton}
                  onClick={toggleMenu}
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect
                      x="3"
                      y="5"
                      width="18"
                      height="2"
                      rx="1"
                      fill={menuIconFill}
                    />
                    <rect
                      x="3"
                      y="11"
                      width="18"
                      height="2"
                      rx="1"
                      fill={menuIconFill}
                    />
                    <rect
                      x="3"
                      y="17"
                      width="18"
                      height="2"
                      rx="1"
                      fill={menuIconFill}
                    />
                  </svg>
                </button>
                {isMenuOpen &&
                  (() => {
                    const menu = (
                      <GameMenu
                        onClose={() => setMenuOpen(false)}
                        playerId={user?.playerId ?? null}
                        positionMode={
                          useViewportFixedMenu
                            ? 'viewport-fixed'
                            : 'header-anchor'
                        }
                        chrome={
                          isSkyxNewRoute(gameName)
                            ? buildSkyxMenuChrome(platformUIConfig)
                            : undefined
                        }
                        handlers={
                          isSkyxNew ? SKYX_NEW_MENU_HANDLERS : undefined
                        }
                        hideAnimationToggle={isSkyxNew}
                        useServerPlayerAvatar={isSkyxNew}
                        i18nGameId={isSkyxNew ? 'skyx_new' : undefined}
                      />
                    );

                    return useViewportFixedMenu ? (
                      createPortal(
                        <div
                          ref={menuPortalRef}
                          data-skyx-new-ui
                          style={{
                            position: 'fixed',
                            inset: 0,
                            zIndex: 10050,
                            pointerEvents: 'none',
                          }}
                        >
                          {menu}
                        </div>,
                        document.body
                      )
                    ) : (
                      <>
                        <div
                          style={{
                            position: 'absolute',
                            top: '32px',
                            right: '6px',
                            width: 0,
                            height: 0,
                            borderLeft: '10px solid transparent',
                            borderRight: '10px solid transparent',
                            borderBottom:
                              '10px solid rgba(255, 255, 255, 0.18)',
                            zIndex: 2001,
                          }}
                        />
                        <div
                          style={{
                            position: 'absolute',
                            top: '34px',
                            right: '8px',
                            width: 0,
                            height: 0,
                            borderLeft: '8px solid transparent',
                            borderRight: '8px solid transparent',
                            borderBottom: `8px solid ${
                              isSkyxNew ? '#140033' : 'rgba(13, 17, 23, 0.95)'
                            }`,
                            zIndex: 2002,
                          }}
                        />
                        {menu}
                      </>
                    );
                  })()}
              </div>
            </>
          )}

          <button
            type="button"
            data-chat-toggle
            className="header-icon-btn"
            style={styles.chatIconButton}
            onClick={handleChatToggle}
            aria-label={t('ui.chat.title')}
          >
            <svg
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M7.8693 18.2834c-.6914 0-.9434-.6153-.5742-1.1075.252-.334.7031-.9902.955-1.4707-1.834-.8906-3.0351-2.6133-3.0351-4.5351 0-3.0235 3.0176-5.4551 6.7852-5.4551 3.7675 0 6.7851 2.4316 6.7851 5.4551 0 3.1289-3.0879 5.5195-7.2187 5.4433-1.2364.8789-2.8828 1.67-3.6973 1.67Z"
                fill={menuIconFill}
              />
            </svg>
            {hasUnreadChat ? (
              isSkyxNew ? (
                <span
                  style={getChatUnreadCountBadgeStyle(
                    '#170550',
                    unreadChatCount
                  )}
                  aria-label={`${formatUnreadChatCount(unreadChatCount)} unread messages`}
                >
                  {formatUnreadChatCount(unreadChatCount)}
                </span>
              ) : (
                <span
                  style={getChatUnreadDotStyle('#1b1c1d')}
                  aria-hidden="true"
                />
              )
            ) : null}
          </button>

          <button
            type="button"
            className="header-icon-btn"
            style={styles.closeIconButton}
            onClick={handleGoLobby}
            aria-label={t('ui.common.close')}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <line
                x1="4"
                y1="4"
                x2="20"
                y2="20"
                stroke={menuIconFill}
                strokeWidth="2.5"
                strokeLinecap="round"
              />
              <line
                x1="20"
                y1="4"
                x2="4"
                y2="20"
                stroke={menuIconFill}
                strokeWidth="2.5"
                strokeLinecap="round"
              />
            </svg>
          </button>

          {!inGame && isDemo && (
            <button style={styles.authButton} onClick={logout}>
              {t('ui.login.go_to_login')}
            </button>
          )}
          {!inGame && !isDemo && (
            <button style={styles.authButton} onClick={handleDemoStart}>
              {t('ui.login.play_demo')}
            </button>
          )}
        </div>
      </div>

      {useStackedSessionHeader && (
        <GamingSessionTimers
          style={{
            width: '100%',
            justifyContent: 'space-between',
          }}
        />
      )}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  headerContainer: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    height: '38.5px',
    background: '#1b1c1d',
    // borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 16px',
    zIndex: 1100,
    pointerEvents: 'auto',
    color: '#e8f0f8',
    userSelect: 'none',
    fontFamily: 'Roboto, sans-serif',
  },
  leftSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  logo: {
    height: '24px',
    transition: 'height 0.2s ease-in-out',
  },
  rightSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    position: 'relative',
  },
  menuButton: {
    borderRadius: '50%',
    width: '28px',
    height: '28px',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '0px',
    boxShadow: 'none',
  },
  menuIconImage: {
    width: '20px',
    height: '20px',
    objectFit: 'contain',
    boxShadow: 'none',
  },
  chatIconButton: {
    position: 'relative',
    borderRadius: '50%',
    width: '28px',
    height: '28px',
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '0px',
    boxShadow: 'none',
    overflow: 'visible',
  },
  closeIconButton: {
    borderRadius: '50%',
    width: '28px',
    height: '28px',
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '0px',
    boxShadow: 'none',
  },
  authButton: {
    padding: '8px 16px',
    borderRadius: '6px',
    border: '1px solid #00e0ff',
    background: 'transparent',
    color: '#00e0ff',
    fontWeight: 'bold',
    cursor: 'pointer',
  },
};
